import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy, Close, Email } from '@mui/icons-material';

const MAX_MAILTO_LENGTH = 1800;

const EmailDialog = ({ open, onClose, subject, body, bodyWithoutTranscript }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const fullContent = `Subject: ${subject}\n\n${body}`;
    navigator.clipboard.writeText(fullContent).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpenEmailClient = () => {
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    const mailtoUrl = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;

    if (mailtoUrl.length <= MAX_MAILTO_LENGTH) {
      window.location.href = mailtoUrl;
    } else {
      // Fallback: open without transcript to stay within URL length limits
      const fallbackBody = bodyWithoutTranscript || body;
      window.location.href = `mailto:?subject=${encodedSubject}&body=${encodeURIComponent(fallbackBody)}`;
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Create Email
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Copy the content below and paste it into your email client, or click{' '}
          <strong>Open in Email Client</strong> to launch your default email app.
        </Typography>

        <TextField
          fullWidth
          label="Subject"
          value={subject}
          InputProps={{ readOnly: true }}
          sx={{ mt: 2, mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={12}
          label="Body"
          value={body}
          InputProps={{ readOnly: true }}
          sx={{ mb: 2 }}
        />

        {copied && (
          <Alert severity="success" sx={{ mb: 1 }}>
            Copied to clipboard!
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Tooltip title="Copy subject and body to clipboard">
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopy}
          >
            Copy to Clipboard
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Email />}
          onClick={handleOpenEmailClient}
        >
          Open in Email Client
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDialog;
